import { useCallback, useState } from 'react';

import type { Candidate } from 'model';

import { Vacancy } from '@/clients/AutoCompleteClient/types';
import { useIsIndeedFlow } from '@/shared/legacy/contexts/CommAgentFlowContext';
import { ModalState } from './RJCommunicationHub.types';
import { determineNextCommunicationStep } from './RJCommunicationHub.useModalNavigation.utils';

export const useModalNavigation = ({
  candidate,
  initialVacancy,
  dontShowMessagePreview,
  dontShowWhatsAppMessagePreview,
  isWaCommunicationAgentEnabled,
}: {
  candidate: Candidate;
  initialVacancy?: Vacancy;
  dontShowMessagePreview: boolean | null;
  dontShowWhatsAppMessagePreview: boolean | null;
  isWaCommunicationAgentEnabled: boolean;
}) => {
  const [modalState, setModalState] = useState<ModalState>(
    initialVacancy ? ModalState.LOADING_GENERATED_MESSAGE : ModalState.ACTIVATE_ROBIN_JR,
  );

  const isIndeedFlow = useIsIndeedFlow();

  const navigateToStep = useCallback((step: ModalState) => {
    setModalState(step);
  }, []);

  const determineNextStep = useCallback(
    (
      dontShowWhatsAppMessagePreviewArg: boolean | null,
      dontShowMessagePreviewArg: boolean | null,
      dontShowQuestionnairePreview: boolean | null,
    ) => {
      if (isWaCommunicationAgentEnabled && candidate?.contact_info?.phone && !dontShowWhatsAppMessagePreviewArg) {
        return ModalState.WHATSAPP;
      }

      return determineNextCommunicationStep({
        hasEmail: !!candidate?.contact_info?.email,
        isIndeedFlow,
        dontShowMessagePreview: dontShowMessagePreviewArg,
        dontShowQuestionnairePreview,
      });
    },
    [candidate?.contact_info, isIndeedFlow, isWaCommunicationAgentEnabled],
  );

  const determineNextStepFromCurrent = useCallback(
    (
      currentState: ModalState,
      dontShowWhatsAppMessagePreviewArg: boolean | null,
      dontShowMessagePreviewArg: boolean | null,
      dontShowQuestionnairePreview: boolean | null,
    ) => {
      if (currentState === ModalState.WHATSAPP) {
        if (candidate?.contact_info?.email && !dontShowMessagePreviewArg) {
          return ModalState.GENERATED_MESSAGE;
        }
        return ModalState.LOADING;
      }

      if (currentState === ModalState.GENERATED_MESSAGE) {
        if (!dontShowQuestionnairePreview) {
          return ModalState.QUESTIONNAIRE_PREVIEW;
        }
        return ModalState.LOADING;
      }

      if (currentState === ModalState.QUESTIONNAIRE_PREVIEW) {
        return ModalState.LOADING;
      }

      return determineNextStep(
        dontShowWhatsAppMessagePreviewArg,
        dontShowMessagePreviewArg,
        dontShowQuestionnairePreview,
      );
    },
    [determineNextStep, candidate?.contact_info?.email],
  );

  const goBack = useCallback(() => {
    switch (modalState) {
      case ModalState.WHATSAPP:
        return setModalState(ModalState.ACTIVATE_ROBIN_JR);
      case ModalState.GENERATED_MESSAGE:
        if (
          isWaCommunicationAgentEnabled &&
          candidate?.contact_info?.phone &&
          dontShowWhatsAppMessagePreview !== true
        ) {
          return setModalState(ModalState.WHATSAPP);
        }
        return setModalState(ModalState.ACTIVATE_ROBIN_JR);
      case ModalState.QUESTIONNAIRE_PREVIEW:
        if (dontShowMessagePreview !== true) {
          return setModalState(ModalState.GENERATED_MESSAGE);
        }
        if (
          isWaCommunicationAgentEnabled &&
          candidate?.contact_info?.phone &&
          dontShowWhatsAppMessagePreview !== true
        ) {
          return setModalState(ModalState.WHATSAPP);
        }
        return setModalState(ModalState.ACTIVATE_ROBIN_JR);
      default:
        return;
    }
  }, [
    modalState,
    dontShowMessagePreview,
    dontShowWhatsAppMessagePreview,
    candidate?.contact_info?.phone,
    isWaCommunicationAgentEnabled,
  ]);

  return {
    modalState,
    setModalState,
    navigateToStep,
    determineNextStep,
    determineNextStepFromCurrent,
    goBack,
  };
};
